'use client';

// Deployments panel for a single built app (deployApp v2 arc — the delta on
// top of the /apps list). Backed by three owner-scoped endpoints in
// apps/server/src/routes/apps.ts:
//   GET  /api/apps/:id              — the app row (status/publish/publicUrl)
//   GET  /api/apps/:id/deployments  — version history (newest first)
//   POST /api/apps/:id/rollback     — re-deploy a prior version as a new one
// Styling deliberately mirrors app/apps/page.tsx (rounded-2xl cards,
// border-border/60, PublishPill, sonner toasts, AlertDialog confirm) so the
// list → detail transition reads as one surface.
import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import {
  ArrowLeft,
  ArrowSquareOut,
  ArrowsClockwise,
  CheckCircle,
  ClockCounterClockwise,
  ShieldWarning,
  Spinner,
  WarningCircle,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useDocumentTitle } from '@/lib/use-document-title';

interface BuiltApp {
  id: string;
  name: string;
  description?: string;
  status: string;
  publishStatus: string;
  publicUrl?: string;
  previewUrl?: string;
  framework?: string;
  createdAt: string;
  updatedAt: string;
}

interface GateSummary {
  name: string;
  status: string;
  detail?: string;
}

interface Deployment {
  id: string;
  version: number;
  status: string;
  target: string;
  liveUrl?: string;
  fileCount?: number;
  sizeBytes?: number;
  sessionId?: string;
  isCurrent: boolean;
  rolledBackFrom?: number;
  takenDown: boolean;
  takedownReason?: string;
  gates?: GateSummary[];
  createdAt: string;
}

function formatBytes(bytes?: number): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function tierLabel(target: string): string {
  if (target === 'cf-workers') return 'Cloudflare Workers';
  if (target === 'static-s3') return 'Static (S3)';
  return target;
}

function statusVariant(status: string) {
  if (status === 'live') return 'default' as const;
  if (status === 'rejected' || status === 'failed' || status === 'taken-down') return 'destructive' as const;
  return 'secondary' as const;
}

function PublishPill({ publishStatus }: { publishStatus: string }) {
  const live = publishStatus === 'live';
  return (
    <span className={`flex items-center gap-1.5 text-[11px] font-semibold ${live ? 'text-emerald-400' : 'text-muted-foreground'}`}>
      <span className={`size-1.5 rounded-full ${live ? 'bg-emerald-400' : 'bg-muted-foreground/60'}`} />
      {live ? 'Live' : 'Unpublished'}
    </span>
  );
}

export function AppDetailClient({ appId }: { appId: string }) {
  const [app, setApp] = useState<BuiltApp | null>(null);
  const [deployments, setDeployments] = useState<Deployment[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rollbackTarget, setRollbackTarget] = useState<Deployment | null>(null);
  const [busyVersion, setBusyVersion] = useState<number | null>(null);
  const [statusMessage, setStatusMessage] = useState('');

  useDocumentTitle(app ? `${app.name} — Deployments` : 'Deployments');

  const load = useCallback(async () => {
    try {
      const [appRes, depRes] = await Promise.all([
        fetch(`/api/apps/${appId}`),
        fetch(`/api/apps/${appId}/deployments`),
      ]);
      if (!appRes.ok) throw new Error(appRes.status === 404 ? 'App not found.' : 'Failed to load app.');
      if (!depRes.ok) throw new Error('Failed to load deployment history.');
      setApp((await appRes.json()) as BuiltApp);
      setDeployments((await depRes.json()) as Deployment[]);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Could not load this app.');
      setDeployments([]);
    }
  }, [appId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRollbackConfirm = async () => {
    const target = rollbackTarget;
    if (!target) return;
    setRollbackTarget(null);
    setBusyVersion(target.version);
    try {
      const res = await fetch(`/api/apps/${appId}/rollback`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ toVersion: target.version }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(typeof data?.error === 'string' ? data.error : `Rollback failed (${res.status})`);
      toast.success(`Rolled back to v${target.version} — now live as v${data?.version ?? '?'}`);
      setStatusMessage(`Rolled back to version ${target.version}.`);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Rollback failed');
    } finally {
      setBusyVersion(null);
    }
  };

  const takenDown = deployments?.some((d) => d.takenDown) ?? false;
  const takedownReason = deployments?.find((d) => d.takenDown)?.takedownReason;
  const current = deployments?.find((d) => d.isCurrent);
  const liveUrl = app?.publicUrl ?? app?.previewUrl ?? current?.liveUrl;
  const currentGates = current?.gates ?? [];

  return (
    <main className="min-h-screen bg-background text-foreground">
      <Toaster position="top-center" richColors />
      <section className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-4 py-8 sm:px-6 lg:px-8">
        <div>
          <Button asChild variant="ghost" size="sm" className="-ml-2 text-muted-foreground">
            <Link href="/apps" className="flex items-center gap-1.5">
              <ArrowLeft weight="bold" className="size-3.5" />
              Apps
            </Link>
          </Button>
        </div>

        <span role="status" aria-live="polite" className="sr-only">
          {statusMessage}
        </span>

        {app === null && !loadError ? (
          <div className="flex min-h-[360px] items-center justify-center">
            <Spinner className="size-8 animate-spin text-muted-foreground" />
          </div>
        ) : loadError ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
            {loadError}
          </div>
        ) : app ? (
          <>
            <header className="flex flex-col gap-4 border-b pb-6 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0 space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Deployments</p>
                <h1 className="truncate text-3xl font-semibold tracking-normal" title={app.name}>
                  {app.name}
                </h1>
                {app.description ? (
                  <p className="max-w-2xl text-sm leading-6 text-muted-foreground">{app.description}</p>
                ) : null}
              </div>
              <Button asChild variant="secondary" size="sm">
                <Link
                  href={`/chat?prompt=${encodeURIComponent(`Continue development on ${app.name}.`)}`}
                  className="flex items-center gap-1.5"
                >
                  <ArrowsClockwise weight="bold" className="size-3.5" />
                  Continue in chat
                </Link>
              </Button>
            </header>

            {/* Takedown honesty banner. */}
            {takenDown ? (
              <div className="flex items-start gap-3 rounded-2xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
                <ShieldWarning weight="fill" className="mt-0.5 size-5 shrink-0" />
                <div className="space-y-1">
                  <p className="font-semibold">This app has been taken down.</p>
                  <p className="text-destructive/90">
                    {takedownReason ?? 'An administrator disabled this app. Its public URL returns 410 and it cannot be rolled back or republished.'}
                  </p>
                </div>
              </div>
            ) : null}

            {/* Status card. */}
            <article className="flex flex-col gap-4 rounded-2xl border border-border/60 bg-card/60 p-5">
              <div className="flex flex-wrap items-center gap-3">
                <Badge variant={statusVariant(app.status === 'active' ? 'live' : app.status)}>{app.status}</Badge>
                <PublishPill publishStatus={app.publishStatus} />
                {current ? <span className="text-[11.5px] text-muted-foreground">current: v{current.version}</span> : null}
              </div>

              <dl className="grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-0.5">
                  <dt className="text-[11.5px] uppercase tracking-wide text-muted-foreground">Live URL</dt>
                  <dd>
                    {liveUrl && app.publishStatus === 'live' ? (
                      <a
                        href={liveUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1 truncate font-medium text-foreground hover:underline"
                        title={liveUrl}
                      >
                        <ArrowSquareOut weight="bold" className="size-3.5 shrink-0" />
                        <span className="truncate">Open live app</span>
                      </a>
                    ) : (
                      <span className="text-muted-foreground">Not published</span>
                    )}
                  </dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-[11.5px] uppercase tracking-wide text-muted-foreground">Tier</dt>
                  <dd className="font-medium">{current ? tierLabel(current.target) : app.framework ?? '—'}</dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-[11.5px] uppercase tracking-wide text-muted-foreground">Last publish</dt>
                  <dd className="font-medium">{current ? new Date(current.createdAt).toLocaleString() : '—'}</dd>
                </div>
                <div className="space-y-0.5">
                  <dt className="text-[11.5px] uppercase tracking-wide text-muted-foreground">Current size</dt>
                  <dd className="font-medium">
                    {current ? `${formatBytes(current.sizeBytes)} · ${current.fileCount ?? '—'} files` : '—'}
                  </dd>
                </div>
              </dl>

              {/* Preflight / quality gates for the current live version. */}
              {currentGates.length > 0 ? (
                <div className="flex flex-wrap gap-2 border-t border-border/60 pt-3">
                  {currentGates.map((g) => {
                    const passed = g.status === 'passed';
                    const skipped = g.status === 'skipped';
                    return (
                      <span
                        key={g.name}
                        title={g.detail}
                        className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                          passed
                            ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                            : skipped
                              ? 'border-border/60 bg-muted/40 text-muted-foreground'
                              : 'border-destructive/40 bg-destructive/10 text-destructive'
                        }`}
                      >
                        {passed ? (
                          <CheckCircle weight="fill" className="size-3.5" />
                        ) : skipped ? null : (
                          <WarningCircle weight="fill" className="size-3.5" />
                        )}
                        {g.name}
                      </span>
                    );
                  })}
                </div>
              ) : null}
            </article>

            {/* Version history. */}
            <div className="space-y-3">
              <h2 className="text-lg font-semibold">Version history</h2>
              {deployments && deployments.length === 0 ? (
                <div className="rounded-2xl border border-dashed px-6 py-10 text-center text-sm text-muted-foreground">
                  No deployments yet. Ask Galexia to deploy this app from chat.
                </div>
              ) : (
                <ul className="flex flex-col gap-2.5">
                  {(deployments ?? []).map((d) => {
                    const busy = busyVersion === d.version;
                    const succeeded = d.status === 'live' || d.status === 'superseded';
                    const canRollback = !d.isCurrent && succeeded && !takenDown;
                    return (
                      <li
                        key={d.id}
                        className={`flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center sm:justify-between ${
                          d.isCurrent ? 'border-emerald-500/40 bg-emerald-500/[0.04]' : 'border-border/60 bg-card/40'
                        }`}
                      >
                        <div className="min-w-0 space-y-1.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[15px] font-bold tracking-tight">v{d.version}</span>
                            <Badge variant={statusVariant(d.status)}>{d.status}</Badge>
                            {d.isCurrent ? (
                              <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-400">
                                <span className="size-1.5 rounded-full bg-emerald-400" />
                                Current
                              </span>
                            ) : null}
                            {d.rolledBackFrom != null ? (
                              <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                                <ClockCounterClockwise weight="bold" className="size-3.5" />
                                rolled back from v{d.rolledBackFrom}
                              </span>
                            ) : null}
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11.5px] text-muted-foreground">
                            <span>{new Date(d.createdAt).toLocaleString()}</span>
                            <span>{tierLabel(d.target)}</span>
                            <span>{formatBytes(d.sizeBytes)} · {d.fileCount ?? '—'} files</span>
                            {d.sessionId ? (
                              <Link href={`/chat/${d.sessionId}`} className="hover:underline">
                                from chat
                              </Link>
                            ) : null}
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {canRollback ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busy || busyVersion !== null}
                              onClick={() => setRollbackTarget(d)}
                            >
                              {busy ? (
                                <Spinner className="size-3.5 animate-spin" />
                              ) : (
                                <ClockCounterClockwise weight="bold" className="size-3.5" />
                              )}
                              Rollback
                            </Button>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        ) : null}
      </section>

      <AlertDialog open={Boolean(rollbackTarget)} onOpenChange={(open) => { if (!open) setRollbackTarget(null); }}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Roll back to v{rollbackTarget?.version}?</AlertDialogTitle>
            <AlertDialogDescription>
              This re-deploys version {rollbackTarget?.version}&rsquo;s content as a new live version. Your history stays
              intact — the current version is kept and can be restored the same way.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleRollbackConfirm()}>Roll back</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
